import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { getUserFromLocalStorage } from '../../services/api';
import messageService from '../../services/messageService';
import './Messages.css';

// 自定义确认对话框组件
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  
  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal">
        <h3 className="custom-modal-title">{title}</h3>
        <p className="custom-modal-message">{message}</p>
        <div className="custom-modal-buttons">
          <button className="custom-modal-button cancel" onClick={onCancel}>
            取消
          </button>
          <button className="custom-modal-button confirm" onClick={onConfirm}>
            确定
          </button>
        </div>
      </div>
    </div>
  );
};

// 自定义复选框组件
const CustomCheckbox = ({ checked, onChange, onClick }) => {
  return (
    <label className="custom-checkbox-container">
      <div 
        className={`custom-checkbox ${checked ? 'checked' : ''}`} 
        onClick={(e) => {
          if (onClick) onClick(e);
        }}
      />
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
      />
    </label>
  );
};

// 默认示例用户数据
const DEFAULT_USER = {
  id: 'default-user-id',
  username: '访客用户',
  avatar: '/images/profile.png'
};

const Messages = () => {
  const [user, setUser] = useState(getUserFromLocalStorage() || DEFAULT_USER);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('system');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const history = useHistory();
  
  // 初始化数据
  useEffect(() => {
    // 加载消息
    const loadMessages = () => {
      // 获取消息列表
      let userMessages = messageService.getMessages(user.id);
      
      // 如果没有消息，创建示例消息
      if (userMessages.length === 0) {
        userMessages = messageService.createSampleMessages(user.id);
      }
      
      // 将所有消息设为已读状态
      if (user && user.id) {
        // 标记所有消息为已读
        messageService.markAllAsRead(user.id);
        userMessages = userMessages.map(msg => ({ ...msg, read: true }));
        
        // 将未读消息数量存入localStorage为0，以便Header组件可以更新
        localStorage.setItem(`${user.id}_unread_messages`, "0");
      }
      
      setMessages(userMessages);
    };
    
    loadMessages();
    
  }, [user, history]);
  
  // 切换标签时加载对应的消息
  useEffect(() => {
    if (user) {
      let filteredMessages = messageService.getMessages(user.id);
      
      // 根据标签筛选消息
      if (activeTab === 'system') {
        filteredMessages = filteredMessages.filter(msg => msg.type === 'system');
      } else if (activeTab === 'interaction') {
        filteredMessages = filteredMessages.filter(msg => msg.type === 'comment' || msg.type === 'reply');
      } else if (activeTab === 'unread') {
        filteredMessages = filteredMessages.filter(msg => msg.type === 'like' || msg.type === 'favorite');
      }
      // 全部消息不需要筛选
      
      setMessages(filteredMessages);
      // 退出选择模式
      setIsSelectionMode(false);
      setSelectedMessageIds([]);
    }
  }, [activeTab, user]);
  
  // 获取未读消息数
  const getUnreadCount = (type = 'all') => {
    const allMessages = messageService.getMessages(user.id);
    if (type === 'all') {
      return allMessages.filter(msg => !msg.read).length;
    } else if (type === 'system') {
      return allMessages.filter(msg => msg.type === 'system' && !msg.read).length;
    } else if (type === 'interaction') {
      return allMessages.filter(msg => (msg.type === 'comment' || msg.type === 'reply') && !msg.read).length;
    } else if (type === 'unread') {
      return allMessages.filter(msg => (msg.type === 'like' || msg.type === 'favorite') && !msg.read).length;
    }
    return 0;
  };
  
  // 处理消息点击
  const handleMessageClick = (message) => {
    if (isSelectionMode) {
      // 选择模式下处理消息选择
      toggleMessageSelection(message.id);
      return;
    }
    
    // 显示消息详情
    setSelectedMessage(message);
    
    // 如果有关联链接，跳转到相关页面
    if (message.relatedLink) {
      window.open(message.relatedLink, '_blank', 'noopener,noreferrer');
    }
  };
  
  // 显示确认对话框
  const showConfirmModal = (title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };
  
  // 关闭确认对话框
  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };
  
  // 标记所有为已读
  const handleMarkAllAsRead = () => {
    showConfirmModal(
      '标记全部已读',
      '确定将所有消息标记为已读吗？',
      () => {
        messageService.markAllAsRead(user.id);
        
        // 更新状态
        setMessages(prevMessages => 
          prevMessages.map(msg => ({ ...msg, read: true }))
        );
      }
    );
  };
  
  // 删除消息
  const handleDeleteMessage = (messageId, e) => {
    e.stopPropagation(); // 阻止事件冒泡
    
    showConfirmModal(
      '删除消息',
      '确定要删除这条消息吗？',
      () => {
        messageService.deleteMessage(messageId);
        
        // 更新状态
        setMessages(prevMessages => 
          prevMessages.filter(msg => msg.id !== messageId)
        );
        
        // 如果当前选中的消息被删除，清除选中状态
        if (selectedMessage && selectedMessage.id === messageId) {
          setSelectedMessage(null);
        }
      }
    );
  };
  
  // 切换选择模式
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedMessageIds([]);
  };
  
  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedMessageIds.length === messages.length) {
      // 如果已全选，则取消全选
      setSelectedMessageIds([]);
    } else {
      // 全选所有消息
      setSelectedMessageIds(messages.map(msg => msg.id));
    }
  };
  
  // 切换单个消息的选择状态
  const toggleMessageSelection = (messageId) => {
    setSelectedMessageIds(prevSelected => {
      if (prevSelected.includes(messageId)) {
        // 取消选择
        return prevSelected.filter(id => id !== messageId);
      } else {
        // 选择
        return [...prevSelected, messageId];
      }
    });
  };
  
  // 批量删除选中的消息
  const deleteSelectedMessages = () => {
    if (selectedMessageIds.length === 0) return;
    
    showConfirmModal(
      '批量删除',
      `确定要删除选中的 ${selectedMessageIds.length} 条消息吗？`,
      () => {
        // 逐个删除选中的消息
        selectedMessageIds.forEach(messageId => {
          messageService.deleteMessage(messageId);
        });
        
        // 更新消息列表
        setMessages(prevMessages => 
          prevMessages.filter(msg => !selectedMessageIds.includes(msg.id))
        );
        
        // 重置选择状态
        setSelectedMessageIds([]);
        
        // 如果当前没有消息了，退出选择模式
        if (messages.length - selectedMessageIds.length === 0) {
          setIsSelectionMode(false);
        }
      }
    );
  };
  
  // 格式化日期时间
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 0) {
      return `${diffDay}天前`;
    } else if (diffHour > 0) {
      return `${diffHour}小时前`;
    } else if (diffMin > 0) {
      return `${diffMin}分钟前`;
    } else {
      return '刚刚';
    }
  };

  return (
    <div className="elegant-app">
      <Header />
      <main className="elegant-main">
        <div className="messages-container">
          <div className="messages-title">
            消息中心
            {getUnreadCount() > 0 && (
              <span className="messages-badge">{getUnreadCount()}</span>
            )}
            {getUnreadCount() > 0 && !isSelectionMode && (
              <button 
                className="mark-all-read-button"
                onClick={handleMarkAllAsRead}
              >
                全部已读
              </button>
            )}
          </div>
          
          <div className="messages-tabs">
            <div 
              className={`messages-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => !isSelectionMode && setActiveTab('all')}
            >
              全部消息
              {getUnreadCount('all') > 0 && (
                <span className="tab-badge">{getUnreadCount('all')}</span>
              )}
            </div>
            <div 
              className={`messages-tab ${activeTab === 'system' ? 'active' : ''}`}
              onClick={() => !isSelectionMode && setActiveTab('system')}
            >
              系统通知
              {getUnreadCount('system') > 0 && (
                <span className="tab-badge">{getUnreadCount('system')}</span>
              )}
            </div>
            <div 
              className={`messages-tab ${activeTab === 'interaction' ? 'active' : ''}`}
              onClick={() => !isSelectionMode && setActiveTab('interaction')}
            >
              评论与回复
              {getUnreadCount('interaction') > 0 && (
                <span className="tab-badge">{getUnreadCount('interaction')}</span>
              )}
            </div>
            <div 
              className={`messages-tab ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => !isSelectionMode && setActiveTab('unread')}
            >
              赞与被购买
            </div>
            
            {messages.length > 0 && (
              <div className="messages-actions">
                {isSelectionMode ? (
                  <>
                    <button 
                      className="select-button"
                      onClick={toggleSelectAll}
                    >
                      {selectedMessageIds.length === messages.length ? '取消全选' : '全选'}
                    </button>
                    <button 
                      className="delete-selected-button"
                      onClick={deleteSelectedMessages}
                      disabled={selectedMessageIds.length === 0}
                    >
                      删除
                    </button>
                    <button 
                      className="cancel-select-button"
                      onClick={toggleSelectionMode}
                    >
                      取消
                    </button>
                  </>
                ) : (
                  <button 
                    className="select-button"
                    onClick={toggleSelectionMode}
                  >
                    多选
                  </button>
                )}
              </div>
            )}
          </div>
          
          {messages.length > 0 ? (
            <div className="messages-list">
              {messages.map(message => (
                <div 
                  key={message.id}
                  className={`message-item ${!message.read ? 'unread' : ''} ${selectedMessageIds.includes(message.id) ? 'selected' : ''}`}
                  onClick={() => handleMessageClick(message)}
                >
                  {isSelectionMode && (
                    <div className="message-checkbox">
                      <div className={`custom-checkbox ${selectedMessageIds.includes(message.id) ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedMessageIds.includes(message.id)}
                          onChange={() => toggleMessageSelection(message.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  )}
                  <div className="message-avatar">
                    <img 
                      src={message.sender.avatar || process.env.PUBLIC_URL + '/images/profile.png'} 
                      alt={message.sender.name} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = process.env.PUBLIC_URL + '/images/profile.png';
                      }}
                    />
                  </div>
                  <div className="message-content">
                    <div className="message-header">
                      <h3 className="message-title">{message.title}</h3>
                      <span className="message-date">{formatDateTime(message.date)}</span>
                    </div>
                    <p className="message-text">{message.content}</p>
                    {message.relatedLink && (
                      <a 
                        href={message.relatedLink}
                        className="message-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        查看详情
                      </a>
                    )}
                  </div>
                  {!message.read && <div className="unread-indicator"></div>}
                  {!isSelectionMode && (
                    <div 
                      className="message-delete-button" 
                      onClick={(e) => handleDeleteMessage(message.id, e)}
                      title="删除消息"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-messages">
              <p>暂无消息</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      
      {/* 确认对话框 */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default Messages; 